import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, Check, XCircle, Trophy, Clock, ArrowRight, ArrowUpCircle, AlertTriangle, Undo2 } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useUser } from '@/contexts/UserContext';
import api from '@/lib/api';
import { toast } from 'sonner';

const statusConfig = {
  pending:           { label:'Pendiente',     color:'bg-yellow-600/80 text-white border-0' },
  changes_requested: { label:'Mejora pedida', color:'bg-orange-500/80 text-white border-0' },
  accepted:          { label:'Aceptada',      color:'bg-green-600/80 text-white border-0' },
  rejected:          { label:'Rechazada',     color:'bg-red-600/80 text-white border-0' },
  withdrawn:         { label:'Retirada',      color:'bg-zinc-500/80 text-white border-0' },
  completed:         { label:'Completada',    color:'bg-blue-600/80 text-white border-0' },
};

export default function DealsPage() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const navigate = useNavigate();
  const user = useUser();

  const fetchOffers = () => {
    api.get('/offers/my').then(r => { setOffers(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { fetchOffers(); }, []);

  const received = offers.filter(o => o.seller_id === user?.user_id);
  const sent = offers.filter(o => o.buyer_id === user?.user_id);

  const handleAccept = async (offerId) => {
    try { await api.put(`/offers/${offerId}/accept`); toast.success('Oferta aceptada!'); setShowSuccess(true); setTimeout(() => setShowSuccess(false), 3000); fetchOffers(); }
    catch { toast.error('Error'); }
  };
  const handleReject = async (offerId) => {
    try { await api.put(`/offers/${offerId}/reject`); toast.success('Oferta rechazada permanentemente'); fetchOffers(); }
    catch { toast.error('Error'); }
  };
  const handleRequestChanges = async (offerId) => {
    try { await api.put(`/offers/${offerId}/request-changes`); toast.success('Mejora solicitada'); fetchOffers(); }
    catch { toast.error('Error'); }
  };
  const handleWithdraw = async (offerId) => {
    try { await api.put(`/offers/${offerId}/withdraw`); toast.success('Oferta retirada'); fetchOffers(); }
    catch { toast.error('Error'); }
  };
  const handleComplete = async (offerId) => {
    try { await api.put(`/offers/${offerId}/complete`); toast.success('Trato completado!'); setShowSuccess(true); setTimeout(() => setShowSuccess(false), 3000); fetchOffers(); }
    catch { toast.error('Error'); }
  };

  const OfferCard = ({ offer, isReceived }) => {
    const config = statusConfig[offer.status] || statusConfig.pending;
    return (
      <div data-testid={`deal-card-${offer.offer_id}`} className="bg-card border-2 border-border rounded-sm p-4 space-y-3">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <Badge className={`rounded-sm text-[10px] font-body ${config.color}`}>{config.label}</Badge>
            <h4 className="font-body font-bold text-foreground text-sm mt-2 truncate">
              {isReceived ? `Oferta de ${offer.buyer_name}` : `Tu oferta por ${offer.target_item_title}`}
            </h4>
          </div>
          {offer.target_item_image && (
            <div className="w-14 h-14 bg-zinc-900 rounded-sm overflow-hidden shrink-0 ml-3">
              <img src={offer.target_item_image} className="object-cover w-full h-full" alt="" />
            </div>
          )}
        </div>

        <div className="space-y-1 text-sm font-body">
          <div className="flex justify-between text-muted-foreground"><span>Objetivo:</span><span className="text-foreground">{offer.target_item_title} (${offer.target_item_price?.toLocaleString()})</span></div>
          {offer.offered_item_title && <div className="flex justify-between text-muted-foreground"><span>Ofrecido:</span><span className="text-foreground">{offer.offered_item_title} (${offer.offered_item_price?.toLocaleString()})</span></div>}
          {offer.cash_amount > 0 && <div className="flex justify-between text-muted-foreground"><span>Ribete:</span><span className="text-primary font-bold">${offer.cash_amount?.toLocaleString()} MXN</span></div>}
        </div>

        <div className="flex flex-wrap gap-2 pt-2">
          {/* SELLER actions on pending offers */}
          {isReceived && offer.status === 'pending' && (
            <>
              <Button data-testid={`deal-accept-${offer.offer_id}`} onClick={() => handleAccept(offer.offer_id)} size="sm" className="flex-1 rounded-sm font-body font-bold uppercase text-xs bg-green-600 hover:bg-green-700 text-white">
                <Check className="w-4 h-4 mr-1" /> ACEPTAR
              </Button>
              <Button data-testid={`deal-request-changes-${offer.offer_id}`} onClick={() => handleRequestChanges(offer.offer_id)} size="sm" className="flex-1 rounded-sm font-body font-bold uppercase text-xs bg-orange-500 hover:bg-orange-600 text-white">
                <ArrowUpCircle className="w-4 h-4 mr-1" /> PEDIR MEJOR
              </Button>
              <Button data-testid={`deal-reject-${offer.offer_id}`} onClick={() => handleReject(offer.offer_id)} size="sm" variant="outline" className="flex-1 rounded-sm font-body font-bold uppercase text-xs border-destructive text-destructive hover:bg-destructive hover:text-white">
                <XCircle className="w-4 h-4 mr-1" /> RECHAZAR
              </Button>
            </>
          )}

          {/* SELLER sees changes_requested status */}
          {isReceived && offer.status === 'changes_requested' && (
            <div className="flex items-center gap-2 text-orange-400 text-xs font-body"><AlertTriangle className="w-4 h-4" /> Esperando que mejore su oferta...</div>
          )}

          {/* BUYER actions on pending/changes_requested */}
          {!isReceived && (offer.status === 'pending' || offer.status === 'changes_requested') && (
            <>
              <Button data-testid={`deal-improve-${offer.offer_id}`}
                onClick={() => navigate(`/deal/${offer.target_item_id}?offerId=${offer.offer_id}&cash=${offer.cash_amount || ''}&itemId=${offer.offered_item_id || ''}`)}
                size="sm" className="flex-1 rounded-sm font-body font-bold uppercase text-xs bg-primary text-primary-foreground">
                <ArrowUpCircle className="w-4 h-4 mr-1" /> MEJORAR OFERTA
              </Button>
              {offer.status === 'pending' && (
                <Button data-testid={`deal-withdraw-${offer.offer_id}`} onClick={() => handleWithdraw(offer.offer_id)}
                  size="sm" variant="outline" className="rounded-sm font-body font-bold uppercase text-xs border-zinc-500 text-zinc-400 hover:bg-zinc-700 hover:text-white">
                  <Undo2 className="w-4 h-4 mr-1" /> RETIRAR
                </Button>
              )}
              {offer.status === 'changes_requested' && (
                <p className="w-full text-orange-400 text-xs font-body"><AlertTriangle className="w-3 h-3 inline mr-1" />El vendedor pidio mejor oferta</p>
              )}
              {offer.status === 'pending' && (
                <p className="w-full text-muted-foreground text-xs font-body flex items-center gap-1"><Clock className="w-3 h-3" /> Esperando respuesta...</p>
              )}
            </>
          )}

          {/* Accepted state — banner + chat + complete */}
          {offer.status === 'accepted' && (
            <>
              <div data-testid={`deal-accepted-banner-${offer.offer_id}`} className="w-full bg-green-600/20 border border-green-600/30 rounded-sm p-2.5 text-center mb-1">
                <p className="font-body text-xs text-green-400 font-bold">Trato cerrado! Usa el chat para coordinar la entrega.</p>
              </div>
              <Button data-testid={`deal-chat-${offer.offer_id}`} onClick={() => navigate(`/chat/${offer.offer_id}`)} size="sm" variant="outline" className="flex-1 rounded-sm font-body text-xs">
                <MessageCircle className="w-4 h-4 mr-1" /> CHAT
              </Button>
              <Button data-testid={`deal-complete-${offer.offer_id}`} onClick={() => handleComplete(offer.offer_id)} size="sm" className="flex-1 rounded-sm font-body font-bold uppercase text-xs bg-primary text-primary-foreground">
                <Trophy className="w-4 h-4 mr-1" /> COMPLETAR
              </Button>
            </>
          )}

          {/* Chat link for active negotiations */}
          {(offer.status === 'pending' || offer.status === 'changes_requested') && (
            <Button data-testid={`deal-chat-link-${offer.offer_id}`} onClick={() => navigate(`/chat/${offer.offer_id}`)} size="sm" variant="ghost" className="w-full rounded-sm font-body text-xs text-muted-foreground">
              <MessageCircle className="w-3 h-3 mr-1" /> Ir al chat
            </Button>
          )}

          {offer.status === 'completed' && (
            <>
              <div className="flex items-center gap-2 text-green-500 text-xs font-body font-bold w-full"><Trophy className="w-4 h-4" /> TRATO CERRADO!</div>
              <Button data-testid={`deal-chat-completed-${offer.offer_id}`} onClick={() => navigate(`/chat/${offer.offer_id}`)} size="sm" variant="ghost" className="w-full rounded-sm font-body text-xs text-muted-foreground">
                <MessageCircle className="w-3 h-3 mr-1" /> Ver chat
              </Button>
            </>
          )}
          {offer.status === 'rejected' && <div className="flex items-center gap-2 text-red-400 text-xs font-body"><XCircle className="w-4 h-4" /> Rechazada permanentemente</div>}
          {offer.status === 'withdrawn' && <div className="flex items-center gap-2 text-zinc-400 text-xs font-body"><Undo2 className="w-4 h-4" /> Oferta retirada</div>}
        </div>
      </div>
    );
  };

  return (
    <div data-testid="deals-page" className="min-h-screen bg-background page-with-nav">
      <div className="max-w-md mx-auto px-4">
        <h1 className="font-heading text-3xl text-primary uppercase mb-6">MIS TRATOS</h1>

        {showSuccess && (
          <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center animate-slide-up" onClick={() => setShowSuccess(false)}>
            <div className="text-center space-y-6 p-8">
              <div className="w-24 h-24 bg-primary rounded-sm flex items-center justify-center mx-auto"><Trophy className="w-12 h-12 text-primary-foreground" /></div>
              <h2 className="font-heading text-4xl text-primary">TRATO CERRADO!</h2>
              <p className="text-foreground font-body">Coordina el intercambio por chat.</p>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <Tabs defaultValue="received" className="w-full">
            <TabsList data-testid="deals-tabs" className="w-full bg-card border-2 border-border rounded-sm h-12">
              <TabsTrigger data-testid="deals-tab-received" value="received" className="flex-1 rounded-sm font-body font-bold uppercase text-xs data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">RECIBIDAS ({received.length})</TabsTrigger>
              <TabsTrigger data-testid="deals-tab-sent" value="sent" className="flex-1 rounded-sm font-body font-bold uppercase text-xs data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">ENVIADAS ({sent.length})</TabsTrigger>
            </TabsList>
            <TabsContent value="received" className="space-y-3 mt-4">
              {received.length === 0 ? <div className="text-center py-12 text-muted-foreground font-body"><p>No tienes ofertas recibidas</p></div> : received.map(o => <OfferCard key={o.offer_id} offer={o} isReceived />)}
            </TabsContent>
            <TabsContent value="sent" className="space-y-3 mt-4">
              {sent.length === 0 ? (
                <div className="text-center py-12 space-y-4"><p className="text-muted-foreground font-body">No has enviado ofertas</p>
                  <Button onClick={() => navigate('/')} className="rounded-sm font-body uppercase text-xs">EXPLORAR FEED <ArrowRight className="w-4 h-4 ml-1" /></Button></div>
              ) : sent.map(o => <OfferCard key={o.offer_id} offer={o} isReceived={false} />)}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}
